// Small pure formatting helpers shared across pages/components.

/** parseCoord splits a "@scope/name" coordinate into its parts (without the '@'). */
export function parseCoord(pkg: string): { scope: string; name: string } {
  const stripped = pkg.startsWith("@") ? pkg.slice(1) : pkg;
  const slash = stripped.indexOf("/");
  if (slash < 0) return { scope: stripped, name: "" };
  return { scope: stripped.slice(0, slash), name: stripped.slice(slash + 1) };
}

/** shortDigest shortens "sha256:<hex>" to the first 7 hex chars for display. */
export function shortDigest(digest: string): string {
  const hex = digest.includes(":") ? digest.slice(digest.indexOf(":") + 1) : digest;
  return hex.slice(0, 7);
}

/** formatDate renders an ISO timestamp as a short local date, or "" if unparseable. */
export function formatDate(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

/** splitCsv splits a comma-separated string into trimmed, non-empty tokens. */
export function splitCsv(s: string | undefined | null): string[] {
  return (s ?? "")
    .split(",")
    .map((t) => t.trim())
    .filter((t) => t !== "");
}

/** formatCount renders a count compactly: 980 → "980", 48200 → "48.2k", 1_200_000 → "1.2M". */
export function formatCount(n: number | undefined | null): string {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v) || v <= 0) return "0";
  if (v < 1_000) return String(Math.round(v));
  if (v < 1_000_000) {
    // Round to one decimal first so 999_999 rolls over to "1M", never "1000k".
    const k = Math.round(v / 100) / 10;
    return k >= 1000 ? "1M" : trimZero(k) + "k";
  }
  return trimZero(Math.round(v / 100_000) / 10) + "M";
}

function trimZero(n: number): string {
  return n.toFixed(1).replace(/\.0$/, "");
}

/** formatBytes renders a byte count compactly: 512 → "512 B", 2048 → "2.0 KB". */
export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 1024) return `${Math.max(0, Math.round(n))} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = n / 1024;
  let u = 0;
  while (v >= 1024 && u < units.length - 1) {
    v /= 1024;
    u++;
  }
  return `${v.toFixed(1)} ${units[u]}`;
}

/** relativeTime renders an ISO timestamp as a short "2d ago"-style label, or "" if unparseable. */
export function relativeTime(iso: string | undefined | null): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const secs = Math.max(0, (Date.now() - then) / 1000);
  const units: [number, string][] = [
    [31_536_000, "y"],
    [2_592_000, "mo"],
    [604_800, "w"],
    [86_400, "d"],
    [3_600, "h"],
    [60, "m"],
  ];
  for (const [size, suffix] of units) {
    if (secs >= size) return `${Math.floor(secs / size)}${suffix} ago`;
  }
  return "just now";
}
